// Story 1.27 — admin detail + approve/reject controls. Wrapped by
// <AdminGuard> in App.tsx.
//
// Two-column layout (stacks on mobile): applicant detail (email,
// verified status, account-created, linked URLs as clickable links)
// on the left, the live certificate preview (the real
// <PatronageCertificate> artifact) on the right. Action bar at the
// bottom with Approve + Reject buttons (modals confirm).

import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PatronageCertificate,
} from "@greenhold/components";

import { useAdminCreatorApplicationDetail } from "../features/admin/useAdminCreatorApplicationDetail";
import { useApproveCreatorApplication } from "../features/admin/useApproveCreatorApplication";
import { useRejectCreatorApplication } from "../features/admin/useRejectCreatorApplication";

const REJECTION_REASON_MIN_CHARS = 20;
const REJECTION_REASON_MAX_CHARS = 1000;

/** Lower-cased twitch.tv handle from a linked-account URL, or "" if the
 *  URL isn't a canonical single-segment twitch.tv/<handle>. Used to
 *  decide whether a LISTED Twitch URL is the one that was verified. */
function twitchHandleOf(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname.replace(/^www\./, "").toLowerCase() !== "twitch.tv") return "";
    const seg = u.pathname.split("/").filter(Boolean);
    return seg.length === 1 ? seg[0].toLowerCase() : "";
  } catch {
    return "";
  }
}

function isYoutubeHost(hostname: string): boolean {
  const host = hostname.replace(/^www\./, "").toLowerCase();
  return host === "youtube.com" || host === "m.youtube.com";
}

/** Lower-cased YouTube @handle (without the @), or "".
 *
 *  DELIBERATELY REJECTS the legacy /c/Name and /user/Name forms — they
 *  are a DIFFERENT namespace from @handles, so /user/foo and @foo can be
 *  two unrelated channels. Accepting them could paint a green "ownership
 *  verified" badge next to a channel the applicant does not own. Not
 *  matching is the safe failure; a false match is not. (Mirrors
 *  `youtube_handle_from_url` in creator_identity_service.rs.) */
function youtubeHandleOf(url: string): string {
  try {
    const u = new URL(url);
    if (!isYoutubeHost(u.hostname)) return "";
    const seg = u.pathname.split("/").filter(Boolean);
    if (!seg.length || !seg[0].startsWith("@")) return "";
    return seg[0].slice(1).toLowerCase();
  } catch {
    return "";
  }
}

/** The immutable UC… channel id from a /channel/UC… URL, or "". Case is
 *  PRESERVED: channel ids are case-sensitive. */
function youtubeChannelIdOf(url: string): string {
  try {
    const u = new URL(url);
    if (!isYoutubeHost(u.hostname)) return "";
    const seg = u.pathname.split("/").filter(Boolean);
    if (seg.length < 2 || seg[0] !== "channel") return "";
    return /^UC[A-Za-z0-9_-]{22}$/.test(seg[1]) ? seg[1] : "";
  } catch {
    return "";
  }
}

type IdentityClaimView = {
  provider: string;
  login: string;
  provider_account_id: string;
  verified_at: string;
  method: string;
};

/** Does a LISTED linked-account URL point at a channel the applicant
 *  actually proved ownership of? Provider-dispatched; an unknown
 *  provider matches nothing rather than guessing. */
function isVerifiedLink(
  kind: string,
  url: string,
  claims: IdentityClaimView[],
): boolean {
  const k = kind.toLowerCase();
  return claims.some((c) => {
    if (c.provider !== k) return false;
    if (c.provider === "twitch") {
      return twitchHandleOf(url) === c.login.toLowerCase();
    }
    if (c.provider === "youtube") {
      // Either identifier proves it: the handle the claim carries, or
      // the immutable id the claim is keyed on.
      const handle = youtubeHandleOf(url);
      const channelId = youtubeChannelIdOf(url);
      return (
        (!!handle && handle === c.login.toLowerCase()) ||
        (!!channelId && channelId === c.provider_account_id)
      );
    }
    return false;
  });
}

/** Story 8.9 — the applicant's declared capacity, or an honest absence.
 *
 *  `business_status` is NOT a boolean: NULL means no declaration was made, and
 *  it must never render as "a private individual". The server requires the
 *  field on every new submission, so a null is either a row that predates
 *  migration 0094 or one written outside the apply endpoint — in both cases
 *  "not declared" is the true statement. */
function businessStatusLabel(status: string | null): string {
  if (status === "business") return "a business";
  if (status === "private") return "a private individual";
  return "not declared";
}

const PROVIDER_LABELS: Record<string, string> = {
  twitch: "Twitch",
  youtube: "YouTube",
  kick: "Kick",
};

export function AdminApplicationDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useAdminCreatorApplicationDetail(id);
  const approveMutation = useApproveCreatorApplication(id);
  const rejectMutation = useRejectCreatorApplication(id);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  if (query.isPending) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-brand-paper-200 text-brand-forest-900">
        <p className="opacity-70 text-sm font-mono">Loading application…</p>
      </main>
    );
  }

  if (query.isError) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-brand-paper-200 text-brand-forest-900 p-6">
        <Card className="max-w-md">
          <CardContent className="p-6 space-y-3 text-center">
            <p className="text-destructive">{query.error.message}</p>
            <Link
              to="/admin/applications"
              className="underline decoration-dotted"
            >
              Back to queue
            </Link>
          </CardContent>
        </Card>
      </main>
    );
  }

  const detail = query.data.data;
  const isPending = detail.status === "pending";

  async function handleApprove() {
    try {
      const result = await approveMutation.mutateAsync();
      toast.success(
        `Approved ${result.data.assigned_handle ? `@${result.data.assigned_handle}` : detail.display_name}`
      );
      setShowApproveModal(false);
      navigate("/admin/applications");
    } catch (err) {
      const e = err as { message?: string; current_status?: string };
      const suffix = e.current_status ? ` (current: ${e.current_status})` : "";
      toast.error(`${e.message ?? "Approve failed"}${suffix}`);
    }
  }

  async function handleReject() {
    try {
      await rejectMutation.mutateAsync(rejectReason);
      toast.success(`Rejected ${detail.display_name}`);
      setShowRejectModal(false);
      setRejectReason("");
      navigate("/admin/applications");
    } catch (err) {
      const e = err as { message?: string; field?: string };
      toast.error(
        e.field ? `${e.message} (field: ${e.field})` : (e.message ?? "Reject failed")
      );
    }
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <Link
            to="/admin/applications"
            className="text-sm underline decoration-dotted opacity-70 hover:opacity-100"
          >
            ← Back to queue
          </Link>
          <Badge
            variant={
              detail.status === "pending"
                ? "warning"
                : detail.status === "approved"
                  ? "success"
                  : detail.status === "rejected"
                    ? "destructive"
                    : "secondary"
            }
          >
            {detail.status}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: applicant detail */}
          <Card>
            <CardHeader>
              <CardTitle>{detail.display_name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <span className="opacity-60">Email: </span>
                <span className="font-mono">{detail.applicant_email}</span>{" "}
                {detail.applicant_email_verified ? (
                  <Badge variant="success">verified</Badge>
                ) : (
                  <Badge variant="destructive">unverified</Badge>
                )}
              </div>
              <div>
                <span className="opacity-60">Account created: </span>
                {new Date(detail.applicant_account_created_at).toLocaleString()}
              </div>
              <div>
                <span className="opacity-60">Submitted: </span>
                {new Date(detail.submitted_at).toLocaleString()}
              </div>
              <div>
                <span className="opacity-60">Region: </span>
                {detail.region} · {detail.language}
              </div>
              {detail.preview_slug ? (
                <div>
                  {/* Handles still exist (internal identifier + /u/ vault
                      pages), but /creators/{handle} public profile pages
                      are gone — label it as the handle it becomes. */}
                  <span className="opacity-60">Handle on approval: </span>
                  <code className="font-mono text-xs">
                    @{detail.preview_slug}
                  </code>
                </div>
              ) : null}
              {detail.applicant_existing_handle ? (
                <div>
                  <span className="opacity-60">Existing handle: </span>
                  <code className="font-mono text-xs">
                    @{detail.applicant_existing_handle}
                  </code>
                </div>
              ) : null}

              {detail.prior_consent_withdrawals.length > 0 ? (
                <div
                  className="border-l-4 border-state-warning-accent bg-state-warning-fill/30 pl-3 py-2 pr-2 rounded-r"
                  data-testid="prior-withdrawals-warning"
                >
                  <p className="font-semibold text-sm mb-1">
                    ⚠ Withdrew consent before
                  </p>
                  <p className="text-xs opacity-80 mb-2">
                    This applicant was an approved creator and withdrew from{" "}
                    {detail.prior_consent_withdrawals.length === 1
                      ? "a dossier"
                      : `${detail.prior_consent_withdrawals.length} dossiers`}
                    , which revoked their creator status:
                  </p>
                  <ul className="space-y-1 text-sm">
                    {detail.prior_consent_withdrawals.map((w) => (
                      <li key={`${w.portfolio_id}-${w.withdrawn_at}`}>
                        <Link
                          to={`/admin/portfolios/${w.portfolio_id}`}
                          className="underline decoration-dotted hover:decoration-solid"
                        >
                          {w.portfolio_title}
                        </Link>{" "}
                        <span className="opacity-60">
                          — withdrawn{" "}
                          {new Date(w.withdrawn_at).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* Story 8.9 — the identity block. An application submitted
                  before migration 0094 has none of it, and SAYS SO: blank
                  rows here would read as a capture bug on a live form, and
                  the reviewer needs to know the difference between "the
                  applicant left it out" (impossible — the server requires
                  it) and "we never asked".

                  Story 8.11 — there are now TWO reasons the block is empty,
                  and they are not interchangeable. An erased account (the
                  applicant exercised Art. 17; the identity was nulled with
                  the rest of their personal data) must NOT be reported as an
                  application that predates capture: that is a false statement
                  about the platform's own records, made to the one person
                  auditing them. `applicant_account_erased` is server-derived
                  from the `@anonymized.greenhold` email sentinel, so this
                  needed no schema change. */}
              <div data-testid="admin-identity-block">
                <p className="opacity-60 mb-1">Identity</p>
                {detail.legal_name ? (
                  <ul className="space-y-1">
                    <li>
                      <span className="opacity-60">Legal name: </span>
                      {detail.legal_name}
                    </li>
                    <li>
                      <span className="opacity-60">Address: </span>
                      {[
                        detail.address_line1,
                        detail.address_line2,
                        detail.postal_code,
                        detail.city,
                        detail.country_code,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </li>
                    <li>
                      <span className="opacity-60">Applying as: </span>
                      {/* Three-way, not a boolean. A row can carry a legal name
                          from a partially-captured era with no declaration on
                          it, and rendering that as "a private individual" would
                          put a capacity the applicant never declared in front of
                          the reviewer deciding how to settle with them. */}
                      {businessStatusLabel(detail.business_status)}
                    </li>
                    {detail.business_registration_number ? (
                      <li>
                        <span className="opacity-60">
                          CVR / VAT registration number:{" "}
                        </span>
                        <code className="font-mono text-xs">
                          {detail.business_registration_number}
                        </code>
                      </li>
                    ) : null}
                  </ul>
                ) : detail.applicant_account_erased ? (
                  /* ⚠ THIS COPY IS DELIBERATELY TRUE OF BOTH ERASED CASES,
                     and it is not the three-branch split it looks like it
                     should be. An erased row and an erased PRE-0094 row are
                     indistinguishable here by construction: erasure nulls all
                     nine columns, so "did this row ever hold an identity"
                     is precisely the fact the erasure destroyed, and the
                     panel has no other source for it. Claiming "these
                     details were erased" for a 2025 application would assert
                     we once held details we never collected — the same class
                     of untruth as the "predates identity capture" line this
                     branch fixes, told to the same auditor. So the sentence
                     asserts only what is certain: the account was deleted,
                     and whatever was captured went with it. Do not "improve"
                     this into a confident split without a real signal —
                     inferring one from `submitted_at` versus a hardcoded
                     migration date would be manufactured evidence. */
                  <p
                    className="text-sm opacity-70"
                    data-testid="admin-identity-erased"
                  >
                    Erased with the account — the applicant deleted their
                    account, and any identity captured here was erased with it.
                    Settlement records keep their own copy.
                  </p>
                ) : (
                  <p
                    className="text-sm opacity-70"
                    data-testid="admin-identity-not-captured"
                  >
                    Not captured — application predates identity capture
                  </p>
                )}
              </div>

              <div>
                <p className="opacity-60 mb-1">Linked accounts</p>
                <ul className="space-y-1">
                  {detail.linked_accounts.map((la, idx) => {
                    const verified = isVerifiedLink(
                      la.kind,
                      la.url,
                      detail.identity_claims,
                    );
                    return (
                      <li key={idx} className="text-sm">
                        <span className="opacity-60 capitalize">{la.kind}: </span>
                        <a
                          href={la.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline decoration-dotted hover:decoration-solid"
                        >
                          {la.url}
                        </a>
                        {verified ? (
                          <Badge variant="success" className="ml-2">
                            ✓ ownership verified
                          </Badge>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Story 1.38 + 7.20 — advisory ownership proofs, one row per
                  provider. Verification isn't required to apply, but a proven
                  claim is the strongest identity signal the reviewer has.
                  Shown even if the proved handle differs from a listed URL
                  (so a mismatch is visible, not hidden). */}
              <div>
                <p className="opacity-60 mb-1">Channel ownership</p>
                {detail.identity_claims.length > 0 ? (
                  <ul className="space-y-1">
                    {detail.identity_claims.map((claim) => (
                      <li
                        key={claim.provider}
                        className="text-sm flex flex-wrap items-center gap-2"
                        data-testid={`admin-${claim.provider}-verified`}
                      >
                        <Badge variant="success">
                          ✓ {PROVIDER_LABELS[claim.provider] ?? claim.provider}{" "}
                          verified
                        </Badge>
                        <span>
                          as <code className="font-mono">@{claim.login}</code>
                          <span className="opacity-60">
                            {" "}
                            ({claim.method === "oauth" ? "OAuth" : claim.method} ·{" "}
                            {new Date(claim.verified_at).toLocaleDateString()})
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p
                    className="text-sm opacity-70"
                    data-testid="admin-ownership-unverified"
                  >
                    No channel ownership proven (advisory — not required).
                  </p>
                )}
              </div>

              {detail.status === "rejected" && detail.rejection_reason ? (
                <div className="border-l-4 border-destructive pl-3 py-1">
                  <p className="opacity-60 text-xs mb-1">
                    Rejection reason (logged):
                  </p>
                  <p className="whitespace-pre-wrap text-sm">
                    {detail.rejection_reason}
                  </p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Right: certificate preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cert preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* The REAL engraved-parchment artifact (<PatronageCertificate>,
                  identical to the marketplace/vault and the applicant's own
                  live preview), fed from the application fields so the admin
                  reviews exactly what will be issued. Placeholder conventions
                  match CreatorApplicationCertPreview: preferred tier (the real
                  tier is decided at each reveal), zeroed serial, today's issue
                  date, generic dossier title, mint condition. */}
              <div className="flex justify-center rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 px-6 py-8">
                <PatronageCertificate
                  width={280}
                  tier="preferred"
                  name={detail.display_name || "Creator"}
                  serial="GH-0000-0000"
                  dossier="New Dossier"
                  issued={new Date().toISOString().slice(0, 10)}
                  portraitUrl={detail.image_signed_url}
                  signatureUrl={detail.signature_signed_url}
                  quote={detail.hello_text || ""}
                  condition={0}
                />
              </div>
              <p className="font-mono text-xs uppercase tracking-widest opacity-50 text-center">
                Preview · tier is decided at each reveal ceremony
              </p>
              {!detail.signature_signed_url && (
                <p className="text-xs italic opacity-50 text-center">
                  No signature on file.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action bar */}
        {isPending ? (
          <Card>
            <CardContent className="flex flex-wrap gap-3 justify-end p-4">
              <Button
                variant="destructive"
                onClick={() => setShowRejectModal(true)}
              >
                Reject
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowApproveModal(true)}
              >
                Approve
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="text-center opacity-70 p-4 text-sm">
              This application has already been decided. No further actions
              available.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Approve modal */}
      {showApproveModal ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Approve {detail.display_name}?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm opacity-80">
                This sends the welcome email, grants the creator dashboard,
                and assigns the handle{" "}
                <code>@{detail.preview_slug ?? "…"}</code>. Their profile
                stays visible to admins and themselves only.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setShowApproveModal(false)}
                  disabled={approveMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                >
                  {approveMutation.isPending ? "Approving…" : "Confirm approve"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Reject modal */}
      {showRejectModal ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <CardTitle>Reject {detail.display_name}?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="block text-sm">
                <span className="block mb-1 opacity-70">
                  Rejection reason ({REJECTION_REASON_MIN_CHARS}-
                  {REJECTION_REASON_MAX_CHARS} characters; the applicant sees
                  this verbatim)
                </span>
                <textarea
                  className="w-full border rounded px-2 py-1 bg-white min-h-[120px] font-mono text-sm"
                  value={rejectReason}
                  onChange={(e) =>
                    setRejectReason(e.target.value.slice(0, REJECTION_REASON_MAX_CHARS))
                  }
                  maxLength={REJECTION_REASON_MAX_CHARS}
                />
                <span className="block mt-1 text-xs opacity-60">
                  {rejectReason.length}/{REJECTION_REASON_MAX_CHARS}
                </span>
              </label>
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setShowRejectModal(false)}
                  disabled={rejectMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleReject}
                  disabled={
                    rejectMutation.isPending ||
                    rejectReason.length < REJECTION_REASON_MIN_CHARS ||
                    rejectReason.trim() !== rejectReason
                  }
                >
                  {rejectMutation.isPending
                    ? "Rejecting…"
                    : "Confirm reject"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </main>
  );
}
